import React from "react";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { APILA } from "../../../../../config/apila";
import { APIMS } from "../../../../../config/apims";
import { faBookOpenReader, faMagnifyingGlass, faFilter, faCalendarDays, faEye } from "@fortawesome/free-solid-svg-icons";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import swal from "sweetalert";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PulseLoader } from "react-spinners";
import ModalRapor from "./modal/rapor";
import ModalTglRapor from "./modal/cetak_tanggal_rapor";

/* ===========================
   🔥 SKELETON SHIMMER STYLE
=========================== */
const shimmerStyle = `
  @keyframes shimmer {
    0% { background-position: -468px 0; }
    100% { background-position: 468px 0; }
  }

  .shimmer {
    animation: shimmer 1.2s linear infinite forwards;
    background: linear-gradient(
      to right,
      #ddeaff 8%,
      #f4f8ff 18%,
      #ddeaff 33%
    );
    background-size: 800px 104px;
  }
`;

export default function Dimensi() {
  document.title = "Rapor";

  const [savingId, setSavingId] = useState(null);

  // paging param (untuk master data list)
  const [page] = useState(1);
  const [limit] = useState(1000);
  const [ascending] = useState(1);
  const [descending] = useState(0);
  const [keyword] = useState("");
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const firstLoadDone = useRef(false);

  const storageLevel = localStorage.getItem("level");
  const storageSchoolId = localStorage.getItem("ysb_school_id");
  const storageBranch = localStorage.getItem("ysb_branch_id");

  // Data master
  const [getDataDimensi, setGetDataDimensi] = useState([]);
  const [getDataSemesterMap, setGetDataSemesterMap] = useState([]);

  // Master: Tingkatan + Kelas
  const [getDataLevel, setGetDataLevel] = useState([]);
  const [getDataKelas, setGetDataKelas] = useState([]);
  const [loadingLevel, setLoadingLevel] = useState(false);
  const [loadingKelas, setLoadingKelas] = useState(false);

  // Table data
  const [getData, setGetData] = useState([]);
  const [getDataKop, setGetDataKop] = useState([]);

  // Search (frontend)
  const [searchKeyword, setSearchKeyword] = useState("");

  // UI state
  const [hasSearched, setHasSearched] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const [namaTA, setNamaTA] = useState("");
  const [namaSmstr, setNamaSmstr] = useState("");

  // Filter form
  const [form2, setForm2] = useState({
    ysb_semester_id: "",
    ysb_dimensi_id: "",
    ysb_grade_level_id: "", 
    ysb_kelas_id: "",      
    ysb_branch_id: "",
    ysb_school_id: "",
    ysb_teacher_id: "",
  });

  // Permission
  const location = useLocation();
  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const level = localStorage.getItem("level");
    const storageItems = JSON.parse(localStorage.getItem("role_permission")) || [];
    if (level === "developer" || level === "superadmin") {
      setPermission({ create: 1, read: 1, update: 1, delete: 1 });
      return;
    }
    const foundPermission = storageItems.find((item) => item.menu?.url === location.pathname);
    if (!foundPermission) navigate("/dashboard");
    else {
      setPermission({
        create: foundPermission.create,
        read: foundPermission.read,
        update: foundPermission.update,
        delete: foundPermission.delete,
      });
    }
  }, [location.pathname, navigate]);

  /* API HEADERS */
  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  // ---------- helper refs ----------
  const isMounted = useRef(true);
  const abortControllers = useRef([]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      abortControllers.current.forEach((c) => c.abort());
    };
  }, []);

  const collator = useMemo(() => new Intl.Collator("id", { numeric: true, sensitivity: "base" }), []);

  const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

  // =========================
  // INIT: Semester + Dimensi
  // =========================
  const fetchInit = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();
    abortControllers.current.push(controller);

    try {
      const [semesterRes, dimensiRes] = await Promise.all([
        // APILA.get(
        //   `/api/access/semesters?page=${page}&limit=${limit}&ascending=${descending}&search=${keyword}`,
        //   { ...fetchParams, signal: controller.signal }
        // ),
        APIMS.get(
          `/api/access/semesters?page=${page}&limit=${limit}&ascending=${descending}&search=${keyword}`,
          { ...fetchParams, signal: controller.signal }
        ),
        APILA.get(
          `/api/access/dimensis?page=${page}&limit=${limit}&ascending=${ascending}&search=${keyword}`,
          { ...fetchParams, signal: controller.signal }
        ),
      ]);

      if (!isMounted.current) return;

      // Semester aktif
      if (semesterRes?.status === 200) {
        const semesters = semesterRes.data.data || [];
        const active = semesters.find((s) => s.status === 1) || semesters[0];
        setGetDataSemesterMap(semesters);
        if (active) {
          setNamaTA(active.name_year || "");
          setNamaSmstr(active.semester || "");
          setForm2((prev) => ({ ...prev, ysb_semester_id: active.id }));
        }
      }

      // Dimensi aktif pertama
      if (dimensiRes?.status === 200) {
        const dims = (dimensiRes.data.data || []).filter((d) => d.status === 1);
        setGetDataDimensi(dims);
        if (dims.length > 0) setForm2((prev) => ({ ...prev, ysb_dimensi_id: dims[0].id }));
      }
    } catch (err) {
      swal({
        title: "Failed",
        text: err?.response?.data?.message || err.message || "Error",
        icon: "error",
      });
    } finally {
      setLoading(false); // ⬅️ shimmer BARU MATI DI SINI
    }
  }, [ascending, descending, fetchParams, keyword, limit, page]);

  useEffect(() => {
    fetchInit();
  }, [fetchInit]);

  // =========================
  // Fetch master: Tingkatan (berdasarkan ysb_school_id) + Kelas (berdasarkan tingkatan)
  // =========================
  const fetchLevels = useCallback(async () => {
    // wajib ada schoolId
    if (!storageSchoolId) return;

    const controller = new AbortController();
    abortControllers.current.push(controller);

    setLoadingLevel(true);

    try {
      const levelRes = await APIMS.get(
        `/api/grade-levels?page=${page}&limit=${limit}&ascending=${ascending}&branch=${storageBranch || ""}&level=${storageLevel || ""}&ysb_school_id=${storageSchoolId}`,
        { ...fetchParams, signal: controller.signal }
      );

      if (!isMounted.current) return;

      if (levelRes?.status === 200) {
        let levels = levelRes.data?.data || [];

        const hasSchoolField = levels.some((x) => x?.ysb_school_id);
        if (hasSchoolField) {
          levels = levels.filter((x) => {
            const sid = x?.ysb_school_id;
            return String(sid) === String(storageSchoolId);
          });
        }

        // Sort A -> Z (numeric aware: SD 1, SD 2, SMP 7, dst)
        levels.sort((a, b) => collator.compare(safeStr(a?.name_level), safeStr(b?.name_level)));
        setGetDataLevel(levels);

        // Default: pilih tingkatan pertama saat pertama kali load
        if (levels.length > 0) {
          setForm2((prev) => (prev?.ysb_grade_level_id ? prev : { ...prev, ysb_grade_level_id: levels[0].id }));
        }
      }
    } catch (err) {
      swal({
        title: "Gagal",
        text: err?.response?.data?.message || err.message || "Gagal mengambil master tingkatan",
        icon: "error",
      });
    } finally {
      setLoadingLevel(false);
    }
  }, [ascending, collator, fetchParams, limit, page, storageBranch, storageLevel, storageSchoolId]);

  const fetchKelasByLevel = useCallback(
    async (gradeLevelId) => {
      if (!storageSchoolId || !gradeLevelId) return;

      const controller = new AbortController();
      abortControllers.current.push(controller);

      setLoadingKelas(true);

      try {
        // NOTE: tetap kirim branch & level untuk kompatibilitas, + grade_level_id untuk sesuai request
        const kelasRes = await APIMS.get(
          `/api/kelas?page=${page}&limit=${limit}&ascending=${ascending}&branch=${storageBranch || ""}&level=${storageLevel || ""}&ysb_school_id=${storageSchoolId}&ysb_grade_level_id=${gradeLevelId}`,
          { ...fetchParams, signal: controller.signal }
        );

        if (!isMounted.current) return;

        if (kelasRes?.status === 200) {
          let kelas = kelasRes.data?.data || [];

          // Filter by school (frontend) jika field tersedia
          const hasSchoolField = kelas.some((x) => x?.ysb_school_id);
          if (hasSchoolField) {
            kelas = kelas.filter((x) => {
              const sid = x?.ysb_school_id;
              return String(sid) === String(storageSchoolId);
            });
          }

          // Filter by grade level (frontend) jika field tersedia
          const hasLevelField = kelas.some(
            (x) =>
              x?.ysb_grade_level_id
          );
          if (hasLevelField) {
            kelas = kelas.filter((k) => {
              const kelasLevelId =
                k?.ysb_grade_level_id
              return String(kelasLevelId) === String(gradeLevelId);
            });
          }

          kelas.sort((a, b) => collator.compare(safeStr(a?.name_kelas), safeStr(b?.name_kelas)));
          setGetDataKelas(kelas);

          // Default: pilih kelas pertama (berdasarkan tingkatan terpilih)
          setForm2((prev) => {
            const currentId = prev?.ysb_kelas_id;
            const exists = !!currentId && kelas.some((k) => String(k?.id) === String(currentId));
            if (exists) return prev;
            return { ...prev, ysb_kelas_id: kelas[0]?.id || "" };
          });
        }
      } catch (err) {
        swal({
          title: "Gagal",
          text: err?.response?.data?.message || err.message || "Gagal mengambil master kelas",
          icon: "error",
        });
      } finally {
        setLoadingKelas(false);
      }
    },
    [ascending, collator, fetchParams, limit, page, storageBranch, storageLevel, storageSchoolId]
  );

  useEffect(() => {
    fetchLevels();
  }, [fetchLevels]);

  // Kelas selalu ngikut tingkatan yang dipilih
  useEffect(() => {
    if (!form2.ysb_grade_level_id) return;
    fetchKelasByLevel(form2.ysb_grade_level_id);
  }, [form2.ysb_grade_level_id, fetchKelasByLevel]);


  // =========================
  // Filter complete (untuk tombol Cari)
  // NOTE: tingkatan & kelas sengaja OPTIONAL agar tidak mengubah behavior sebelumnya.
  // =========================
  const isFilterComplete =
    isFilterDirty &&
    !!form2.ysb_semester_id &&
    !!form2.ysb_dimensi_id &&
    !!form2.ysb_grade_level_id &&
    !!form2.ysb_kelas_id;

  // =========================
  // Fetch table data
  // =========================
  // const GetResponseData = useCallback(async () => {
  //   if (!form2.ysb_semester_id || !form2.ysb_dimensi_id) return;

  //   const params = {
  //     ysb_semester_id: form2?.ysb_semester_id,
  //     level: storageLevel,
  //     branch: storageBranch,
  //     // ysb_dimensi_id: form2?.ysb_dimensi_id,
  //     ysb_school_id: storageSchoolId,
  //     // filter tambahan (kalau backend support akan otomatis)
  //     ysb_grade_level_id: form2?.ysb_grade_level_id || undefined,
  //     ysb_kelas_id: form2?.ysb_kelas_id || undefined,
  //   };

  //   try {
  //     const response = await APILA.get("/api/rapot-head/head", {
  //       params,
  //       ...fetchParams,
  //     });

  //     if (response?.status === 200) {
  //       setGetData(response.data.data || []);
  //       setGetDataKop(response.data.data_kop || []);
  //     }
  //   } catch (error) {
  //     swal({
  //       title: "Gagal",
  //       text: error?.response?.data?.message || "Gagal mengambil data",
  //       icon: "error",
  //     });
  //   }
  // }, [form2, fetchParams, storageBranch, storageLevel, storageSchoolId]);

  const GetResponseData = useCallback(async () => {
  if (!form2.ysb_semester_id || !form2.ysb_dimensi_id) return null;

  const params = {
    ysb_semester_id: form2?.ysb_semester_id,
    level: storageLevel,
    branch: storageBranch,
    ysb_school_id: storageSchoolId,
    ysb_grade_level_id: form2?.ysb_grade_level_id || undefined,
    ysb_kelas_id: form2?.ysb_kelas_id || undefined,
  };

  try {
    const response = await APILA.get("/api/rapot-head/head", {
      params,
      ...fetchParams,
    });

    if (response?.status === 200) {
      const latestData = response.data.data || [];
      const latestKop = response.data.data_kop || [];

      setGetData(latestData);
      setGetDataKop(latestKop);

      return {
        data: latestData,
        data_kop: latestKop,
      };
    }

    return null;
  } catch (error) {
    swal({
      title: "Gagal",
      text: error?.response?.data?.message || "Gagal mengambil data",
      icon: "error",
    });

    return null;
  }
}, [form2, fetchParams, storageBranch, storageLevel, storageSchoolId]);

  // AUTO SELECT TAB (tetap dipertahankan)
  useEffect(() => {
    if (getData.length > 0 && getData[0]?.data_element?.length > 0 && !selectedTab) {
      setSelectedTab(getData[0].data_element[0].name_element);
    }
  }, [getData, selectedTab]);

  // FIRST LOAD: tampilkan table otomatis setelah default filter lengkap
  useEffect(() => {
    if (
      firstLoadDone.current ||
      !form2.ysb_semester_id ||
      !form2.ysb_dimensi_id ||
      !form2.ysb_grade_level_id ||
      !form2.ysb_kelas_id
    )
      return;

    firstLoadDone.current = true;

    (async () => {
      setIsFetching(true);
      await GetResponseData(); // ✅ HANYA SEKALI
      setHasSearched(true);
      setIsFirstLoad(false);
      setIsFilterDirty(false);
      setIsFetching(false);
    })();
  }, [
    form2.ysb_semester_id,
    form2.ysb_dimensi_id,
    form2.ysb_grade_level_id,
    form2.ysb_kelas_id,
    GetResponseData,
  ]);

  // Reset tab kalau filter berubah
  useEffect(() => {
    setSelectedTab(null);
  }, [form2.ysb_semester_id, form2.ysb_dimensi_id, form2.ysb_grade_level_id, form2.ysb_kelas_id]);

  // =========================
  // HANDLER perubahan filter (select HTML)
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm2((prev) => {
      // kalau tingkatan berubah, reset kelas
      if (name === "ysb_grade_level_id") {
        return { ...prev, ysb_grade_level_id: value, ysb_kelas_id: "" };
      }
      return { ...prev, [name]: value };
    });

    if (!isFirstLoad) {
      setIsFilterDirty(true);
      setHasSearched(false);
    }
  };

  // =========================
  // BUTTON CARI
  // =========================
  const handleCari = async () => {
    // validasi (biar user jelas)
    if (!form2.ysb_semester_id || !form2.ysb_dimensi_id) {
      swal({
        title: "Filter belum lengkap",
        text: "Pastikan Semester dan Dimensi sudah dipilih.",
        icon: "warning",
      });
      return;
    }

    setSelectedTab(null);
    setGetData([]);
    setHasSearched(false);
    setIsFetching(true);
    setSearchKeyword("");

    try {
      await GetResponseData();
      setHasSearched(true);
      setIsFilterDirty(false);
    } catch (err) {
      swal({
        title: "Failed",
        text: err?.response?.data?.message || "Terjadi kesalahan",
        icon: "error",
      });
    } finally {
      setIsFetching(false);
    }
  };

  // const [showModal, setShowModal] = useState(false);
  // const [selectedRapor, setSelectedRapor] = useState(null);
  // const [dataKopRapor, setdataKopRapor] = useState(null);

  // const viewModalRapor = (id, rapor) => {
  //   try {
  //     setSelectedRapor(rapor || null);
  //     setShowModal(true);
  //     setdataKopRapor(getDataKop);
  //   } catch (err) {
  //     console.error("viewModalRapor error", err);
  //   }
  // };

  const [showModal, setShowModal] = useState(false);
  const [selectedRapor, setSelectedRapor] = useState(null);
  const [dataKopRapor, setdataKopRapor] = useState(null);
  const [showModalTanggal, setShowModalTanggal] = useState(false);

  const viewModalRapor = (id, rapor) => {
    try {
      // tutup dulu sebentar agar modal tidak membawa state murid sebelumnya
      setShowModal(false);

      setTimeout(() => {
        setSelectedRapor(rapor || null);
        setdataKopRapor(getDataKop || null);
        setShowModal(true);
      }, 0);
    } catch (err) {
      console.error("viewModalRapor error", err);
    }
  };

  const handleCloseModalRapor = () => {
    setShowModal(false);
    setSelectedRapor(null);
    setdataKopRapor(null);
  };

  const viewModalTanggal = () => {
    setShowModalTanggal(true);
  };

  const handleTanggalRaporSaved = async () => {
    const latestResult = await GetResponseData();

    if (latestResult?.data_kop) {
      setGetDataKop(latestResult.data_kop);

      // kalau modal Lihat Rapor sedang terbuka, datanya ikut langsung diperbarui
      if (showModal && selectedRapor) {
        setdataKopRapor(latestResult.data_kop);
      }
    }

    setShowModalTanggal(false);
  };


  // =========================
  // Derived: opsi kelas (bila data kelas punya foreign key tingkatan)
  // =========================
  const kelasOptions = useMemo(() => {
    if (!form2.ysb_grade_level_id) return getDataKelas;

    const levelId = form2.ysb_grade_level_id;
    return (getDataKelas || []).filter((k) => {
      const kelasLevelId =
        k?.ysb_grade_level_id;

      // kalau field tidak ada, jangan disaring (biar tetap tampil semua)
      if (!kelasLevelId) return true;
      return String(kelasLevelId) === String(levelId);
    });
  }, [form2.ysb_grade_level_id, getDataKelas]);

  // =========================
  // Nama Wali Kelas (diambil dari data kelas yang dipilih jika tersedia)
  // =========================
  const selectedKelasObj = useMemo(() => {
    if (!form2.ysb_kelas_id) return null;
    return (getDataKelas || []).find((k) => String(k?.id) === String(form2.ysb_kelas_id)) || null;
  }, [form2.ysb_kelas_id, getDataKelas]);

  const waliKelasName = useMemo(() => {
    // Sesuai request: tampilkan wali kelas dari field `ysb_name_teacher`
    const fromKelas =
      selectedKelasObj?.ysb_name_teacher ||
      "";

    const fromData =
      (Array.isArray(getData) &&
        getData.length > 0 &&
        (getData[0]?.ysb_name_teacher || getData[0]?.name_teacher || getData[0]?.teacher_name)) ||
      "";

    return safeStr(fromKelas || fromData);
  }, [getData, selectedKelasObj]);

  // =========================
  // Filter + Search + Sorting (frontend)
  // - Filter tingkatan/kelas diterapkan dari frontend (jika field tersedia di data rapor)
  // - Nama siswa diurutkan A -> Z
  // =========================
  // const displayedData = useMemo(() => {
  //   let rows = Array.isArray(getData) ? [...getData] : [];

  //   // Filter Tingkatan (jika field ada di row)
  //   if (form2.ysb_grade_level_id) {
  //     const selectedLevelId = String(form2.ysb_grade_level_id);
  //     rows = rows.filter((r) => {
  //       const rowLevelId =
  //         r?.ysb_grade_level_id;
  //       if (!rowLevelId) return true; // kalau field tidak ada, jangan dibuang
  //       return String(rowLevelId) === selectedLevelId;
  //     });
  //   }

  //   // Filter Kelas (jika field ada di row)
  //   if (form2.ysb_kelas_id) {
  //     const selectedKelasId = String(form2.ysb_kelas_id);
  //     rows = rows.filter((r) => {
  //       const rowKelasId =
  //         r?.ysb_kelas_id;
  //       if (!rowKelasId) return true;
  //       return String(rowKelasId) === selectedKelasId;
  //     });
  //   }

  //   // Search (Nama siswa / NISN)
  //   const q = safeStr(searchKeyword).trim().toLowerCase();
  //   if (q) {
  //     rows = rows.filter((r) => {
  //       const name = safeStr(r?.name_student).toLowerCase();
  //       const nisn = safeStr(r?.nisn).toLowerCase();
  //       return name.includes(q) || nisn.includes(q);
  //     });
  //   }

  //   // Sort nama siswa A -> Z
  //   rows.sort((a, b) => collator.compare(safeStr(a?.name_student), safeStr(b?.name_student)));

  //   return rows;
  // }, [collator, form2.ysb_grade_level_id, form2.ysb_kelas_id, getData, searchKeyword]);


  const displayedData = useMemo(() => {
      let rows = Array.isArray(getData) ? [...getData] : [];
  
      // Filter Tingkatan (jika field ada di row)
      if (form2.ysb_grade_level_id) {
        const selectedLevelId = String(form2.ysb_grade_level_id);
        rows = rows.filter((r) => {
          const rowLevelId = r?.ysb_grade_level_id;
          if (!rowLevelId) return true;
          return String(rowLevelId) === selectedLevelId;
        });
      }
  
      // Filter Kelas (jika field ada di row)
      if (form2.ysb_kelas_id) {
        const selectedKelasId = String(form2.ysb_kelas_id);
        rows = rows.filter((r) => {
          const rowKelasId = r?.ysb_kelas_id;
          if (!rowKelasId) return true;
          return String(rowKelasId) === selectedKelasId;
        });
      }
  
      // Search Nama siswa / NISN
      const q = safeStr(searchKeyword).trim().toLowerCase();
      if (q) {
        rows = rows.filter((r) => {
          const name = safeStr(r?.name_student).toLowerCase();
          const nisn = safeStr(r?.nisn).toLowerCase();
          return name.includes(q) || nisn.includes(q);
        });
      }
  
      // SORT:
      // siswa lama A-Z di atas, siswa baru order_absen di bawah
      rows.sort((a, b) => {
        const orderA =
          a?.order_absen === null ||
          a?.order_absen === undefined ||
          a?.order_absen === ""
            ? null
            : Number(a.order_absen);
  
        const orderB =
          b?.order_absen === null ||
          b?.order_absen === undefined ||
          b?.order_absen === ""
            ? null
            : Number(b.order_absen);
  
        const aIsNew = orderA !== null && !Number.isNaN(orderA);
        const bIsNew = orderB !== null && !Number.isNaN(orderB);
  
        if (!aIsNew && bIsNew) return -1;
        if (aIsNew && !bIsNew) return 1;
  
        if (aIsNew && bIsNew) {
          if (orderA !== orderB) return orderA - orderB;
  
          const nameCompare = collator.compare(
            safeStr(a?.name_student),
            safeStr(b?.name_student)
          );
  
          if (nameCompare !== 0) return nameCompare;
  
          return collator.compare(
            safeStr(a?.nisn),
            safeStr(b?.nisn)
          );
        }
  
        const nameCompare = collator.compare(
          safeStr(a?.name_student),
          safeStr(b?.name_student)
        );
  
        if (nameCompare !== 0) return nameCompare;
  
        return collator.compare(
          safeStr(a?.nisn),
          safeStr(b?.nisn)
        );
      });
  
      return rows;
    }, [
      collator,
      form2.ysb_grade_level_id,
      form2.ysb_kelas_id,
      getData,
      searchKeyword,
    ]);


  const toTitleCase = (value) => {
    const s = safeStr(value).toLowerCase().trim();
    if (!s) return "";
    return s.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
  };

  return (
    <>
      <style>{shimmerStyle}</style>

      <div className="body rapor-page">
        {showModalTanggal && (
          <ModalTglRapor
            show={showModalTanggal}
            onHide={() => setShowModalTanggal(false)}
            onSaveSuccess={handleTanggalRaporSaved}
            selectedSemesterId={form2.ysb_semester_id}
            selectedSemesterName={
              getDataSemesterMap.find(
                (item) => String(item?.id) === String(form2.ysb_semester_id)
              )?.name_year || namaTA
            }
            selectedSemesterNumber={
              getDataSemesterMap.find(
                (item) => String(item?.id) === String(form2.ysb_semester_id)
              )?.semester || namaSmstr
            }
            branch={storageBranch}
            level={storageLevel}
            schoolId={storageSchoolId}
            GetData={getData}
          />
        )}

        <div className="page-header-card">
          <div className="page-header-top d-flex">
            <div className="page-title-wrap">
              <span className="page-title-icon">
                <FontAwesomeIcon icon={faBookOpenReader} />
              </span>
              <h4 className="page-title-text">Rapor</h4>
            </div>
          </div>

          <nav aria-label="breadcrumb" className="custom-breadcrumb-nav mt-4">
            <ol className="custom-breadcrumb">
              <li className="custom-breadcrumb-item">
                <Link to="/dashboards-head">Beranda</Link>
              </li>
              <li className="custom-breadcrumb-separator">/</li>
              <li className="custom-breadcrumb-item active" aria-current="page">
                Rapor
              </li>
            </ol>
          </nav>
        </div>

        <div className="body-content rapor-content mt-2">
          {/* <div className="rapor-filter-card">
            <div className="rapor-filter-header mb-4 d-flex">
              <div className="rapor-filter-title-wrap">
                <span className="rapor-filter-icon">
                  <FontAwesomeIcon icon={faFilter} />
                </span>
                <div>
                  <h5 className="rapor-filter-title">Filter Rapor</h5>
                  <p className="rapor-filter-subtitle">
                    Pilih semester, tingkatan, dan kelas yang akan ditampilkan.
                  </p>
                </div>
              </div>

              <div className="ml-auto">
                <button
                  type="button"
                  onClick={viewModalTanggal}
                  className="rapor-print-button"
                >
                  <FontAwesomeIcon icon={faCalendarDays} />
                  <span>Cetak Tanggal Rapor</span>
                </button>
              </div>
                
            
              
            </div>

            {loading ? (
              <div className="rapor-filter-grid rapor-filter-grid-head">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div className="rapor-filter-field" key={index}>
                    <div className="shimmer rapor-filter-shimmer-label" />
                    <div className="shimmer rapor-filter-shimmer-control" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="rapor-filter-grid rapor-filter-grid-head">
                <div className="rapor-filter-field">
                  <label className="rapor-filter-label">Tahun Ajaran / Semester</label>
                  <select
                    className="rapor-filter-select"
                    name="ysb_semester_id"
                    value={form2.ysb_semester_id}
                    onChange={handleChange}
                  >
                    <option value="" hidden>
                      {namaTA} (Semester {namaSmstr})
                    </option>
                    {getDataSemesterMap.map((item, index) => (
                      <option key={index} value={item?.id}>
                        {item?.name_year} (Semester {item?.semester})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rapor-filter-field">
                  <label className="rapor-filter-label">Tingkatan</label>
                  <select
                    className="rapor-filter-select"
                    name="ysb_grade_level_id"
                    value={form2.ysb_grade_level_id}
                    onChange={handleChange}
                    disabled={loadingLevel}
                  >
                    <option value="" hidden>
                      {loadingLevel ? "Memuat data tingkatan..." : "Pilih Tingkatan"}
                    </option>
                    {!loadingLevel &&
                      getDataLevel.map((item, index) => (
                        <option key={index} value={item?.id}>
                          {item?.name_level}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="rapor-filter-field">
                  <label className="rapor-filter-label">Kelas</label>
                  <select
                    className="rapor-filter-select"
                    name="ysb_kelas_id"
                    value={form2.ysb_kelas_id}
                    onChange={handleChange}
                    disabled={loadingKelas}
                  >
                    <option value="" hidden>
                      {loadingKelas ? "Memuat data kelas..." : "Pilih Kelas"}
                    </option>
                    {!loadingKelas &&
                      kelasOptions.map((item, index) => (
                        <option key={index} value={item?.id}>
                          {item?.name_kelas}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="rapor-filter-field rapor-filter-action">
                  <button
                    type="button"
                    onClick={handleCari}
                    disabled={!isFilterComplete}
                    className="rapor-filter-button"
                  >
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    Cari Data
                  </button>
                </div>

                
              </div>

              
            )}
          </div> */}

          
          <div className="rapor-filter-card">
  <div className="rapor-filter-toolbar">
    <div className="rapor-filter-list">
      <div className="rapor-filter-item">
        <label>Semester</label>
        <select
          name="ysb_semester_id"
          value={form2.ysb_semester_id}
          onChange={handleChange}
          className="rapor-filter-select"
        >
          <option value="" hidden>
            {namaTA} (Semester {namaSmstr})
          </option>

          {getDataSemesterMap.map((user, index) => (
            <option key={index} value={user?.id}>
              {user?.name_year} (Semester {user?.semester})
            </option>
          ))}
        </select>
      </div>

      <div className="rapor-filter-item">
        <label>Tingkatan</label>
        <select
          name="ysb_grade_level_id"
          value={form2.ysb_grade_level_id}
          onChange={handleChange}
          disabled={loadingLevel}
          className="rapor-filter-select"
        >
          <option value="" hidden>
            {loadingLevel
              ? "Memuat data tingkatan..."
              : "Pilih Tingkatan"}
          </option>

          {!loadingLevel &&
            getDataLevel.map((user, index) => (
              <option key={index} value={user?.id}>
                {user?.name_level}
              </option>
            ))}
        </select>
      </div>

      <div className="rapor-filter-item">
        <label>Kelas</label>
        <select
          name="ysb_kelas_id"
          value={form2.ysb_kelas_id}
          onChange={handleChange}
          disabled={loadingKelas}
          className="rapor-filter-select"
        >
          <option value="" hidden>
            {loadingKelas ? "Memuat data kelas..." : "Pilih Kelas"}
          </option>

          {!loadingKelas &&
            kelasOptions.map((user, index) => (
              <option key={index} value={user?.id}>
                {user?.name_kelas}
              </option>
            ))}
        </select>
      </div>

      <div className="rapor-filter-action">
        <button
          type="button"
          onClick={handleCari}
          disabled={!isFilterComplete}
          className="rapor-btn-search"
        >
          Cari
        </button>
      </div>
    </div>

    {/* PALING KANAN */}
    <div className="rapor-print-action">
      <button
        type="button"
        onClick={viewModalTanggal}
        className="rapor-btn-print"
      >
        <FontAwesomeIcon icon={faCalendarDays} />
                <span>Cetak Tanggal Rapor</span>
      </button>
    </div>
  </div>
</div>

          

          {isFetching && (
            <div className="rapor-loading">
              <PulseLoader color="#3f5bea" size={11} />
            </div>
          )}

          {hasSearched && !isFetching && (
            <>
              {!!waliKelasName && (
                <div className="rapor-class-info">
                  <span className="rapor-class-info-label">Wali Kelas</span>
                  <span className="rapor-class-info-value">
                    {toTitleCase(waliKelasName)}
                  </span>
                </div>
              )}

              <div className="rapor-search-wrap">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="rapor-search-icon"
                />
                <input
                  className="rapor-search-input"
                  placeholder="Cari nama siswa atau NISN..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
              </div>

              <div className="rapor-table-card">
                <div className="rapor-table-scroll">
                  <table className="rapor-table">
                    <thead>
                      <tr>
                        <th className="rapor-col-number">No</th>
                        <th className="rapor-col-name">Nama Siswa</th>
                        <th className="rapor-col-nisn">NISN</th>
                        <th className="rapor-col-action">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedData.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="rapor-empty-state">
                            Data tidak ditemukan
                          </td>
                        </tr>
                      ) : (
                        displayedData.map((rapor, index) => (
                          <tr key={rapor?.ysb_student_id || rapor?.id || index}>
                            <td className="rapor-col-number">
                              <span className="rapor-number-badge">{index + 1}</span>
                            </td>
                            <td className="rapor-col-name">
                              <span className="rapor-student-name">
                                {toTitleCase(rapor?.name_student)}
                              </span>
                            </td>
                            <td className="rapor-col-nisn">
                              <span className="rapor-nisn-text">
                                {rapor?.nisn || "-"}
                              </span>
                            </td>
                            <td className="rapor-col-action">
                              <button
                                type="button"
                                className="rapor-view-button"
                                onClick={() =>
                                  viewModalRapor(
                                    rapor?.ysb_student_id || rapor?.id,
                                    rapor
                                  )
                                }
                              >
                                <FontAwesomeIcon icon={faEye} />
                                <span>Lihat Rapor</span>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {selectedRapor && (
            <ModalRapor
              key={selectedRapor?.ysb_student_id || selectedRapor?.id}
              show={showModal}
              onHide={handleCloseModalRapor}
              data_rapor={selectedRapor}
              data_kop={dataKopRapor}
              id_student={selectedRapor?.ysb_student_id || selectedRapor?.id}
              getData={getData}
            />
          )}
        </div>
      </div>
    </>
  );
}
